/**
 * Client-Side Serial Resolution Hook
 *
 * Encapsulates all client-side serial resolution logic for model derivation
 * and localStorage storage. This keeps client-side code separate from the existing
 * backend API flow to minimize risk of affecting production functionality.
 */

import * as Track from '@axon-enterprise/track';
import { useHistory, useLocation } from 'react-router-dom';

import {
  TEMP_MX_PROPERTIES,
  mixpanelService,
} from '../common/tracking/MixpanelService';
import {
  deriveModelFromSerial,
  isValidSerialFormat,
  normalizeSerial,
} from '../utils/clientSideSerial/serialToModel';
import { SUCCESS } from '../utils/constants';
import { CORE_SETUP_KEYS } from '../utils/localStorage';
import { logger } from '../utils/logger';

const clientSideSerialLogger = logger.withComponent('client-side-serial-flow');

/**
 * Hook for client-side serial resolution
 * Validates, derives model, stores to localStorage, and navigates to guide
 */
export function useClientSideSerial() {
  const history = useHistory();
  const location = useLocation();

  const isV2Route = location.pathname.startsWith('/v2');
  const guideRoute = isV2Route ? '/v2/guide' : '/guide';

  /**
   * Handles the client-side serial resolution flow: pattern-based derivation and navigation
   *
   * @param serial - Device serial number
   * @throws Error if serial format is invalid
   */
  const handleClientSideSerial = async (serial: string): Promise<void> => {
    const normalizedSerial = normalizeSerial(serial);
    const expiresAt = Date.now() + (24 * 60 * 60 * 1000);

    clientSideSerialLogger.debug('Starting client-side serial resolution', {
      serial: normalizedSerial,
      route: guideRoute,
    });

    // Step 1: Validate serial format
    if (!isValidSerialFormat(normalizedSerial)) {
      clientSideSerialLogger.warn('Invalid serial format', { serial: normalizedSerial });
      throw new Error(__('Invalid serial number format. Please check and try again.'));
    }

    // Step 2: Track serial entry
    mixpanelService.track(Track.MxEventName.SearchByDeviceSerialNumber, {
      [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: normalizedSerial,
      [Track.MxPropertyName.Source]: 'client_side_serial',
      [Track.MxPropertyName.Status]: 'initiated',
    });

    // Step 3: Attempt client-side model derivation
    const derivationResult = deriveModelFromSerial(normalizedSerial);

    if (derivationResult.success && derivationResult.model) {
      // Model derived successfully
      clientSideSerialLogger.info('Model derived successfully', {
        serial: normalizedSerial,
        model: derivationResult.model,
        confidence: derivationResult.confidence,
      });

      storeDeviceInfoInLocalStorage(
        normalizedSerial,
        derivationResult.model,
        expiresAt
      );

      mixpanelService.track(Track.MxEventName.SearchByDeviceSerialNumber, {
        [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: normalizedSerial,
        [Track.MxPropertyName.Model]: derivationResult.model,
        [Track.MxPropertyName.Status]: SUCCESS,
      });

      // Navigate to guide (route-aware: /v1/guide or /v2/guide)
      clientSideSerialLogger.info('Navigating to guide', {
        serial: normalizedSerial,
        route: guideRoute,
      });
      history.push(guideRoute);
    } else {
      // Could not derive model - store serial only, guide will show dropdown
      clientSideSerialLogger.info('Model derivation failed, will show dropdown', {
        serial: normalizedSerial,
        reason: derivationResult.reason,
      });

      // Store serial only (no models derived)
      storeDeviceInfoInLocalStorage(normalizedSerial, null, expiresAt);

      // Track that model confirmation is required
      mixpanelService.track(Track.MxEventName.SearchByDeviceSerialNumber, {
        [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: normalizedSerial,
        [Track.MxPropertyName.Status]: 'model_confirmation_required',
      });

      // Navigate to guide (route-aware: will show model selection dropdown)
      clientSideSerialLogger.info('Navigating to guide with dropdown', {
        serial: normalizedSerial,
        route: guideRoute,
      });
      history.push(guideRoute);
    }
  };

  return { handleClientSideSerial };
}

/**
 * Stores device information in localStorage
 *
 * @param serial - Device serial number
 * @param model - Derived model (Lite, Pro, Pro_AI, Elite_AI, Mobile) or null
 * @param expiresAt - Expiration timestamp
 */
function storeDeviceInfoInLocalStorage(
  serial: string,
  model: string | null,
  expiresAt: number
): void {
  // Session ID (synthetic for client-side serial flow)
  localStorage.setItem(CORE_SETUP_KEYS.SESSION_ID, JSON.stringify({
    value: `client_side_serial_${serial}_${Date.now()}`,
    expiresAt,
  }));

  // Device serial number
  localStorage.setItem(CORE_SETUP_KEYS.DEVICE_SERIAL_NUMBER, JSON.stringify({
    value: serial,
    expiresAt,
  }));

  // Device model (used for both display and loading the guide)
  if (model) {
    localStorage.setItem(CORE_SETUP_KEYS.DEVICE_MODEL, JSON.stringify({
      value: model,
      expiresAt,
    }));
    // Also store as derived model for backward compatibility with ConfirmModelStep
    localStorage.setItem(CORE_SETUP_KEYS.DERIVED_MODEL, JSON.stringify({
      value: model,
      expiresAt,
    }));
  } else {
    localStorage.removeItem(CORE_SETUP_KEYS.DEVICE_MODEL);
    localStorage.removeItem(CORE_SETUP_KEYS.DERIVED_MODEL);
  }

  clientSideSerialLogger.debug('Stored device info in localStorage', {
    serial,
    model: model || 'null',
  });
}
